import { useState } from 'react';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { EnterButton } from '@backrs_ui/grow-ui/src/components/Lubav/EnterButton';
import { NumericSubField } from '@backrs_ui/grow-ui/src/models/models';

import { Box, InputBase, SxProps, Theme, Typography } from '@mui/material';

import { SkipChatButton } from './SkipChatButton';

type MultiNumericChatQuestionProps = Readonly<{
  idPrefix?: string;
  options: readonly string[];
  subFields?: readonly NumericSubField[];
  required: boolean;
  isTyping: boolean;
  onSubmit: (answer: string, displayText: string) => void;
  onSkip: () => void;
}>;

const asSubFields = (
  options: readonly string[],
  subFields?: readonly NumericSubField[],
): readonly NumericSubField[] => subFields ?? options.map((label) => ({ field: label, label }));

const outOfRange = ({ minimum, maximum }: NumericSubField, raw: string): boolean => {
  const value = Number(raw);

  if (Number.isNaN(value)) return true;

  return (
    (minimum !== null && minimum !== undefined && value < minimum) ||
    (maximum !== null && maximum !== undefined && value > maximum)
  );
};

const rangeHint = ({ label, minimum, maximum }: NumericSubField): string => {
  if (minimum === null || minimum === undefined || maximum === null || maximum === undefined) {
    return label;
  }

  return `${label} ${minimum}–${maximum}`;
};

export const MultiNumericChatQuestion = ({
  idPrefix = 'multi-numeric-chat-question',
  options,
  subFields,
  required,
  isTyping,
  onSubmit,
  onSkip,
}: MultiNumericChatQuestionProps) => {
  const fields = asSubFields(options, subFields);
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      fields
        .filter((field) => field.resolvedDefault)
        .map((field) => [field.field, String(field.resolvedDefault)]),
    ),
  );

  const handleChange = (key: string, raw: string) => {
    // Only allow digits and a single decimal point.
    if (raw !== '' && !/^\d*\.?\d*$/.test(raw)) return;

    setValues((prev) => ({ ...prev, [key]: raw }));
  };

  const filled = fields.filter((field) => (values[field.field] ?? '').trim() !== '');
  const rejected = filled.filter((field) => outOfRange(field, values[field.field].trim()));
  // Submit should always require at least one filled field — an empty submit conveys
  // no answer. If the question is optional, Skip is the way to move past it.
  const isDisabled = isTyping || filled.length === 0 || rejected.length > 0;

  const handleSubmit = () => {
    if (isDisabled) return;

    const displayText = filled
      .map((field) => `${field.label}: ${values[field.field].trim()}`)
      .join(subFields ? '; ' : ', ');
    // Option labels have no field names to key JSON by, so that answer is the message text itself.
    const answer = subFields
      ? JSON.stringify(
          Object.fromEntries(filled.map((field) => [field.field, values[field.field].trim()])),
        )
      : displayText;

    onSubmit(answer, displayText);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && !isDisabled) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <Box sx={containerSx}>
      <Box sx={cardSx}>
        {fields.map((field, index) => {
          const value = values[field.field] ?? '';
          const hasError = value.trim() !== '' && outOfRange(field, value.trim());

          return (
            <Box key={field.field} sx={rowSx(index < fields.length - 1, hasError)}>
              <Box sx={labelCellSx}>
                <Typography variant="text-md-semibold" sx={labelSx}>
                  {field.label}
                </Typography>
              </Box>
              <InputBase
                id={`${idPrefix}-${field.label.toLowerCase().replace(/\s+/g, '-')}`}
                value={value}
                onChange={(e) => handleChange(field.field, e.target.value)}
                onKeyDown={handleKeyDown}
                inputProps={{ inputMode: 'decimal', 'aria-label': field.label }}
                placeholder="—"
                disabled={isTyping}
                sx={inputSx}
              />
            </Box>
          );
        })}
      </Box>
      {rejected.length > 0 && (
        <Typography variant="text-sm-regular" sx={errorSx}>
          {rejected.map(rangeHint).join(', ')}
        </Typography>
      )}
      <Box sx={actionsRowSx}>
        <EnterButton
          id={`${idPrefix}-submit`}
          onClick={handleSubmit}
          label={appStrings.modules.solutionSessions.questions.submit}
          showEnterHint
          disabled={isDisabled}
        />
        {required ? (
          <Box />
        ) : (
          <SkipChatButton id={`${idPrefix}-skip`} disabled={isTyping} onSkip={onSkip} />
        )}
      </Box>
    </Box>
  );
};

const CARD_WIDTH = '142px'; // Hardcoded: fixed card width from design
const LABEL_CELL_WIDTH = '60px'; // Hardcoded: fixed label column from design

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-md'],
  width: '100%',
});

const cardSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: CARD_WIDTH,
  // The rows read as one card, so their own square corners are clipped by this radius.
  overflow: 'hidden',
  border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
  borderRadius: backrs.radius['radius-xl'],
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  boxShadow: backrs.shadows['shadow-xs'],
  '&:focus-within': {
    boxShadow: backrs.shadows['shadow-focus-ring-brand'],
  },
});

const rowSx =
  (hasRowBelow: boolean, hasError: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    alignItems: 'stretch',
    borderBottom: hasRowBelow
      ? `1px solid ${backrs.colors['Colors/Border/border-primary']}`
      : 'none',
    ...(hasError && {
      backgroundColor: backrs.colors['Colors/Background/bg-error-primary'],
    }),
  });

const labelCellSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
  width: LABEL_CELL_WIDTH,
  padding: `${backrs.spacing['spacing-md']} ${backrs.spacing['spacing-lg']}`,
  borderRight: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
});

const labelSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const inputSx: SxProps<Theme> = ({ backrs }) => ({
  flex: 1,
  padding: `${backrs.spacing['spacing-md']} ${backrs.spacing['spacing-lg']}`,
  color: backrs.colors['Colors/Text/text-primary'],
  '& input::placeholder': {
    color: backrs.colors['Colors/Text/text-placeholder'],
    opacity: 1,
  },
});

const errorSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-error-primary'],
});

const actionsRowSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
};
